import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { toastSuccess, toastWarning } from 'utils/toasts';
import { gqlAddCollectionOrder } from './graphql';
import { FaSave, FaTimesCircle } from 'react-icons/fa';
import * as Yup from 'yup';
import { Formik, Form as FForm, FormikHelpers } from 'formik';

const validationSchema = Yup.object({
  contactPerson: Yup.string()
    .required('Απαιτείται υπεύθυνος παραγγελίας')
    .max(50, 'Μέγιστο 50 χαρακτήρες'),
  notes: Yup.string().max(100, 'Μέγιστο 100 χαρακτήρες'),
});

interface CollectionOrderInput {
  collectionPointId: number;
  contactPerson: string;
  orderedDate: string;
  notes: string;
  collectionOrdersContainers: {
    containertypeId: number;
    containersQty: number;
  }[];
}

const NewCollectionOrderModal = ({
  show,
  onClose,
  collectionPointId,
  initialContainers,
  onOrderCreated,
  selectedOrder,
}) => {
  const initialValues = {
    contactPerson: '',
    orderedDate: new Date().toISOString().split('T')[0],
    notes: '',
  };

  const [containers, setContainers] = useState([]);

  const [addCollectionOrder, { loading }] = useMutation(gqlAddCollectionOrder);

  useEffect(() => {
    if (!selectedOrder && show && initialContainers) {
      setContainers(
        initialContainers.map((container) => ({
          containertypeId: Number(container.containertypeId),
          containersQty: Number(container.containersQty),
        })),
      );
    }
  }, [selectedOrder, show, initialContainers]);

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting, resetForm }: FormikHelpers<typeof initialValues>,
  ) => {
    const input: CollectionOrderInput = {
      collectionPointId,
      contactPerson: values.contactPerson,
      orderedDate: values.orderedDate,
      notes: values.notes,
      collectionOrdersContainers: containers.map((c) => ({
        containertypeId: c.containertypeId,
        containersQty: c.containersQty,
      })),
    };

    try {
      const response = await addCollectionOrder({ variables: { data: input } });
      const newOrderId = response?.data?.addCollectionOrder?.id;
      toastSuccess('Η παραγγελία συλλογής καταχωρήθηκε επιτυχώς.');

      if (onOrderCreated && newOrderId) {
        onOrderCreated(newOrderId);
      }

      resetForm();
      onClose();
    } catch (e) {
      console.error('Error submitting collection order:', e);
      toastWarning(
        <>
          Παρουσιάστηκε κάποιο σφάλμα - η αίτησή σας δεν καταχωρήθηκε.
          <br />
          Παρακαλούμε προσπαθήστε ξανά αργότερα.
          <br />
          {e.message}
        </>,
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleHide = () => {
    onClose();
  };

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>{'Νέα Αίτηση Συλλογής'}</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <FForm>
            <Modal.Body>
              <Form.Group controlId="formContactPerson" className="mt-3">
                <Form.Label>
                  <span style={{ color: 'red' }}>*</span> Υπεύθυνος Αίτησης
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Πληκτρολογήστε το όνομα υπεύθυνου"
                  {...formik.getFieldProps('contactPerson')}
                  isInvalid={formik.touched.contactPerson && !!formik.errors.contactPerson}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.contactPerson}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formDate" className="mt-3">
                <Form.Label>Ημερομηνία Καταχώρησης</Form.Label>
                <Form.Control
                  type="text"
                  value={formik.values.orderedDate.split('-').reverse().join('/')}
                  disabled
                />
              </Form.Group>

              <Form.Group controlId="formNotes" className="mt-3">
                <Form.Label>Σημειώσεις</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Γράψτε σημειώσεις"
                  {...formik.getFieldProps('notes')}
                  isInvalid={formik.touched.notes && !!formik.errors.notes}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.notes}</Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="success"
                type="submit"
                disabled={loading || !formik.isValid || formik.isSubmitting}
                style={{ padding: '0.2rem 2rem' }}
              >
                <FaSave className="mb-1" /> &nbsp; Αποθήκευση
              </Button>
              <Button variant="danger" onClick={onClose} style={{ padding: '0.2rem 2rem' }}>
                <FaTimesCircle className="mb-1" /> &nbsp; Ακύρωση
              </Button>
            </Modal.Footer>
          </FForm>
        )}
      </Formik>
    </Modal>
  );
};

export default NewCollectionOrderModal;
